// OTE (MOM Overtime Exemption) — OFFICE job page (/jobs/:id for OTE jobs; JobRoute picks this page). Laid out like
// the Internal Audit job page on purpose (same tabs, buttons and cards), so the office does the same things the same
// way:
//   Plan & Prepare     Client & quotation (read only) · Job details (Edit → Save draft) · Send & replies
//                      ("📤 Send confirmation" = copy the client email + mark sent, in one click; "Copy client email again")
//   Job & Endorsement  Payment (record any time; "Payment Clearance Check" when sending) · Waiting for the client
//                      (items, day-30 decision) · Reports (MOM endorsement / CCIS report, Resend link) · Records &
//                      history · Session evidence (internal only)
// Every important step goes through the ote-actions server function (it checks the rules and records each step).
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import { toast, failed } from '../lib/toast'
import { oneLine } from '../lib/oneLine'
import { oteJobStatus, sgToday } from '../lib/service'
import { oteAction } from '../lib/ote'
import { cancelContract, reopenContract, lastCancelNote } from '../lib/contractCancel'
import { OPEN_STAGES, stageOf, fmtDay, fmtTime, toTimeInput, otePic, eligibleAuditor, setupComplete, oteNextStep,
  oteConfirmationEmail, EVENT_LABEL, sgDayOf } from '../lib/oteJob'

const fmtDT = d => { try { return new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) } catch { return '—' } }
const KIND = { report: 'CCIS OTE verification report (PDF)', endorsement: 'MOM endorsement (Excel)' }
const STATUS = { preparing: 'Being prepared', issued: 'Ready', error: 'Could not be prepared', superseded: 'Replaced' }
const LEAVE_MSG = 'You have unsaved changes. Are you sure you want to leave without saving?'

export default function OTEJobDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { profile, isAdmin, logAction } = useAuth()
  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [auditors, setAuditors] = useState([])
  const [sigs, setSigs] = useState(new Set())          // auditor ids with a signature on file
  const [acts, setActs] = useState([])
  const [issues, setIssues] = useState([])
  const [reports, setReports] = useState([])
  const [jstate, setJstate] = useState({})
  const [ws, setWs] = useState(null)                   // ote_worksheet (photo consent / no-photo reason)
  const [photo, setPhoto] = useState(null)
  const [history, setHistory] = useState({ events: [], emails: [] })
  const [f, setF] = useState(null)                     // job details form
  const [busy, setBusy] = useState('')
  const [sendTo, setSendTo] = useState('')
  const [payModal, setPayModal] = useState(null)       // 'endorsement' | 'notice' — the Payment Clearance Check
  const [payCleared, setPayCleared] = useState(true)
  const [payReason, setPayReason] = useState('')
  const [jtab, setJtab] = useState(null)
  const [mode, setMode] = useState('view')
  const [newAuditor, setNewAuditor] = useState('')    // Admin: change the auditor after the job has started
  const autoEditFor = useRef(null)

  useEffect(() => { load() }, [id])

  // As Internal Audit: a confirmation not sent yet opens ready to edit (once per job); a sent one opens read-only.
  useEffect(() => {
    if (!job || autoEditFor.current === job.id) return
    autoEditFor.current = job.id
    if (!job.audit_plan_sent_at && job.status !== 'Cancelled' && stageOf(job) === 'scheduled') setMode('edit')
  }, [job])

  const dirty = !!(job && f && (f.audit_date !== (job.audit_date || '') || f.time !== toTimeInput(job.start_time)
    || f.site !== (job.audit_site_address || '') || f.auditor_id !== (job.auditor_id || '')))
  // Unsaved changes: warn before refresh/close and before leaving through an in-app link (as Internal Audit).
  useEffect(() => {
    if (!dirty) return undefined
    const onBeforeUnload = e => { e.preventDefault(); e.returnValue = ''; return '' }
    const onClickCapture = e => {
      const a = e.target.closest && e.target.closest('a[href]')
      if (!a) return
      const href = a.getAttribute('href')
      if (!href || a.target === '_blank' || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return
      if (href === window.location.pathname) return
      if (!window.confirm(LEAVE_MSG)) { e.preventDefault(); e.stopPropagation() }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    document.addEventListener('click', onClickCapture, true)
    return () => { window.removeEventListener('beforeunload', onBeforeUnload); document.removeEventListener('click', onClickCapture, true) }
  }, [dirty])

  async function load() {
    const { data: j } = await supabase.from('jobs').select('*, clients(*)').eq('id', id).maybeSingle()
    if (!j) { setLoading(false); return }
    // First open: the server copies the client's activity rows onto the job (once).
    const { data: w0 } = await supabase.from('ote_worksheet').select('job_id').eq('job_id', id).maybeSingle()
    if (!w0 && j.status !== 'Cancelled') { try { await oteAction({ action: 'init', jobId: id }) } catch (e) { /* shown as no rows */ } }
    const [{ data: aus }, { data: sg }, { data: rows }, { data: iss }, { data: reps }, { data: st }, { data: evs }, { data: mails }, { data: w }, { data: ph }] = await Promise.all([
      supabase.from('auditors').select('id, name, email, active, approval_status, wsh_auditor_expiry, is_auditor').order('name'),
      supabase.from('auditor_signatures').select('auditor_id').is('revoked_at', null),
      supabase.from('ote_activities').select('*').eq('job_id', id).order('sort_order'),
      supabase.from('ote_issues').select('*').eq('job_id', id).order('opened_at'),
      supabase.from('ote_reports').select('id, kind, version, status, error, conclusion, created_at, issued_at').eq('job_id', id).order('created_at', { ascending: false }),
      supabase.from('ote_job_state').select('*').eq('job_id', id).maybeSingle(),
      supabase.from('ote_events').select('event, actor, details, created_at').eq('job_id', id).order('created_at'),
      supabase.from('outbound_emails').select('recipient, subject, category, created_at').like('category', 'ote_%').eq('meta->>job_id', id).order('created_at'),
      supabase.from('ote_worksheet').select('data').eq('job_id', id).maybeSingle(),
      supabase.from('audit_evidence_files').select('id, storage_path, file_name, created_at').eq('job_id', id).eq('evidence_type', 'session_photo').is('archived_at', null).order('created_at', { ascending: false }).limit(1),
    ])
    setJob(j); setAuditors(aus || []); setSigs(new Set((sg || []).map(s => s.auditor_id)))
    setActs(rows || []); setIssues(iss || []); setReports(reps || []); setJstate(st || {}); setWs(w || null); setPhoto(ph?.[0] || null)
    setHistory({ events: evs || [], emails: mails || [] })
    setF({ audit_date: j.audit_date || '', time: toTimeInput(j.start_time), site: j.audit_site_address || '', auditor_id: j.auditor_id || '' })
    setSendTo(otePic(j).email)
    setLoading(false)
  }

  if (loading) return <div style={{ color: 'var(--text3)' }}>Loading…</div>
  if (!job) return <div style={{ color: 'var(--text2)' }}>Job not found.</div>

  const client = job.clients || {}
  const pic = otePic(job)
  const stage = stageOf(job)
  const cancelled = job.status === 'Cancelled'
  // As Internal Audit: the job details stay editable until the report is signed (the site may still be missing);
  // the auditor can only be chosen here before the job starts (after that: Admin "Change auditor").
  const planEditable = !cancelled && OPEN_STAGES.includes(stage)
  const auditorEditable = !cancelled && (stage === 'scheduled' || stage === 'confirmed')
  const aud = auditors.find(a => a.id === job.auditor_id)
  const deadline = jstate.closeout_deadline || null
  const deadlineDue = stage === 'awaiting_closeout' && deadline && sgToday() >= sgDayOf(deadline)   // the deadline DAY, as the reminder email
  const paid = !!job.invoice_clearance_checked_at && !!job.invoice_cleared_confirmed
  const liveRows = acts.filter(r => !r.removed_at)
  const openIssues = issues.filter(i => i.status === 'open')
  const latest = k => reports.find(r => r.kind === k && r.status !== 'superseded')
  const signedStages = ['awaiting_payment', 'ready_to_send', 'sending', 'not_endorsed', 'completed', 'closed', 'void', 'signing']
  const defaultTab = signedStages.includes(stage) || stage === 'awaiting_closeout' ? 'report' : 'plan'
  const tab = jtab || defaultTab
  const signingStuck = stage === 'signing' && (!jstate.stage_at || Date.now() - new Date(jstate.stage_at).getTime() > 10 * 60 * 1000)
  const sendingStuck = stage === 'sending' && (!jstate.stage_at || Date.now() - new Date(jstate.stage_at).getTime() > 10 * 60 * 1000)
  const status = oteJobStatus(job)

  const run = async (key, body, okMsg) => {
    setBusy(key)
    try { const d = await oteAction({ jobId: id, ...body }); if (okMsg) toast(okMsg, 'success', d?.warning ? { detail: d.warning } : undefined); await load(); setBusy(''); return true }
    catch (e) { failed("Couldn't complete that action", e.message); setBusy(''); return false }
  }

  // ── Plan & Prepare ──
  function missingList() {
    const m = []
    if (!f.audit_date) m.push('Job date')
    if (!f.time) m.push('Start time')
    if (!oneLine(f.site).trim()) m.push('Site address')
    if (!f.auditor_id) m.push('WSH auditor')
    return m
  }
  async function saveDraft() {
    const site = oneLine(f.site).trim()
    const keep = f.auditor_id && f.auditor_id === (job.auditor_id || '') ? aud : null
    if (keep && f.audit_date && !eligibleAuditor(keep, f.audit_date)) { toast(`${keep.name} can't do this date`, 'warning', { detail: 'Their WSH auditor registration expires before it (or they are no longer approved). Choose another auditor.' }); return false }
    setBusy('save')
    let saved = false, ok = true
    try {
      const t = f.time ? f.time.replace(':', '') : null
      const { error } = await supabase.from('jobs').update({ audit_date: f.audit_date || null, start_time: t,
        audit_time_display: t ? fmtTime(t) : null, audit_site_address: site || null }).eq('id', id)
      if (error) throw new Error(error.message)
      saved = true
      if (f.auditor_id && f.auditor_id !== (job.auditor_id || '')) {
        const d = await oteAction({ action: 'assign', jobId: id, auditorId: f.auditor_id })
        if (d?.warning) toast('Auditor assigned', 'warning', { detail: d.warning })
        if (d && d.signatureOnFile === false) toast('No signature on file for this auditor', 'warning', { detail: 'Upload it in Personnel → Evidence before the job.' })
      }
      toast('Draft saved', 'success')
    } catch (e) { ok = false; failed(saved ? 'Saved — but the auditor was not assigned' : "Couldn't save", e.message) }
    setBusy(''); await load()
    return ok
  }
  function confirmationMail() {
    return oteConfirmationEmail({ greet: pic.name || client.name, company: client.name, date: job.audit_date,
      time: job.start_time, location: job.audit_site_address, auditor: aud?.name || job.auditor_name })
  }
  async function copyMail(mail) {
    try { await navigator.clipboard.write([new window.ClipboardItem({ 'text/html': new Blob([mail.html], { type: 'text/html' }), 'text/plain': new Blob([mail.text], { type: 'text/plain' }) })]); return true }
    catch { try { await navigator.clipboard.writeText(mail.text); return true } catch { return false } }
  }
  // As Internal Audit "Send audit plan": one click marks it sent AND copies the client email (paste it in Outlook).
  async function sendConfirmation() {
    if (dirty) return toast('Save the draft first', 'warning', { detail: 'Then send the confirmation' })
    const miss = missingList()
    if (miss.length || !setupComplete(job)) return toast('Fill in the missing fields', 'warning', { detail: '• ' + (miss.length ? miss : ['Save the draft']).join('\n• ') })
    setBusy('plan')
    try { await oteAction({ action: 'mark-sent', jobId: id }) } catch (e) { setBusy(''); return failed("Couldn't send the confirmation", e.message) }
    const copied = await copyMail(confirmationMail())
    setBusy('')
    toast('Confirmation sent', copied ? 'success' : 'warning', { detail: copied
      ? `Client email copied — paste it into the email to ${pic.email || 'the client'} (Ctrl+V).\nSubject: ${confirmationMail().subject}`
      : 'Couldn’t copy the client email — use “Copy client email again”' })
    setMode('view')
    load()
  }
  async function copyAgain() { if (await copyMail(confirmationMail())) toast('Client email copied', 'success'); else toast("Couldn't copy", 'error') }

  async function cancelJob() {
    const why = window.prompt('Cancel this contract? The job and its signed quotation will be cancelled.\n\nPlease give a reason:')
    if (why === null) return
    if (!why.trim()) return toast('Enter a reason', 'warning')
    try {
      const r = await cancelContract({ job, reason: why.trim(), by: profile?.name })
      await logAction?.('Contract cancelled', 'job', id, client.name, { reason: why.trim(), quotations: r.quoteRefs })
      toast('Contract cancelled', 'success', { detail: 'Let the auditor, Accounts and the client know' })
    } catch (e) { failed("Couldn't cancel", e.message) }
    load()
  }
  async function reopenJob() {
    if (!window.confirm('Reopen this job? Its quotation goes back to Signed.')) return
    try {
      const r = await reopenContract({ job, status: 'Confirmed', by: profile?.name })
      await logAction?.('Contract reopened', 'job', id, client.name, { quotations: r.quoteRefs })
      toast('Job reopened', 'success')
    } catch (e) { failed("Couldn't reopen", e.message) }
    load()
  }
  const askReason = (what) => { const r = window.prompt(`${what}\n\nPlease give a reason:`); if (r === null) return null; if (!r.trim()) { toast('Enter a reason', 'warning'); return null } return r.trim() }

  async function openReport(r) {
    try { const d = await oteAction({ action: 'report-url', jobId: id, reportId: r.id }); window.open(d.url, '_blank', 'noopener') }
    catch (e) { failed("Couldn't open it", e.message) }
  }
  // "Send endorsement" / "Send notice" — as Internal Audit: the Payment Clearance Check first, unless payment is in.
  async function confirmSend(kind) {
    setPayModal(null)
    if (!job.invoice_clearance_checked_at) {
      if (!(await run('pay', { action: 'record-payment', cleared: payCleared, reason: payReason.trim() }))) return
    }
    const what = kind === 'endorsement' ? 'Endorsement' : 'Notice'
    await run('send', { action: kind === 'endorsement' ? 'send' : 'send-not-endorsed', to: sendTo.trim() },
      job.invoice_clearance_checked_at ? `${what} sent` : payCleared ? `Payment recorded — ${what.toLowerCase()} sent` : `${what} sent before payment`)
  }

  const eligible = auditors.filter(a => a.is_auditor !== false && (eligibleAuditor(a, f?.audit_date) || a.id === job.auditor_id))
  const historyRows = [
    ...history.events.map(e => ({ at: e.created_at, text: (EVENT_LABEL[e.event] || e.event) + (e.details?.reason ? ` — “${e.details.reason}”` : '') + (e.details?.to ? ` — to ${e.details.to}` : '') + (e.actor ? ` · ${e.actor}` : '') })),
  ].sort((a, b) => new Date(a.at) - new Date(b.at))
  const consent = ws?.data?.photo_consent
  const noPhotoReason = String(ws?.data?.no_photo_reason || '').trim()

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ fontSize: 13, marginBottom: 6 }}><span style={{ color: 'var(--brand)', cursor: 'pointer' }} onClick={() => { if (!dirty || window.confirm(LEAVE_MSG)) navigate('/jobs') }}>← Jobs</span></div>
      <div className="page-header" style={{ alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>{client.name || '—'}</h1>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>OTE endorsement{job.quote_ref ? ` · ${job.quote_ref}` : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="badge badge-info">{status}</span>
          {mode === 'view'
            ? <>
                {planEditable && <button className="primary sm" onClick={() => { setJtab('plan'); setMode('edit') }}>Edit</button>}
                {!cancelled && OPEN_STAGES.includes(stage) && <button className="sm" onClick={cancelJob}>Cancel contract</button>}
              </>
            : <>
                <button className="sm" disabled={busy === 'save'} onClick={async () => { if (await saveDraft()) setMode('view') }} style={{ background: 'none', color: 'var(--text2)' }}>Save &amp; close</button>
                <button className="sm" onClick={async () => { await load(); setMode('view') }}>Cancel</button>
              </>}
        </div>
      </div>
      {cancelled && (
        <div style={{ background: 'var(--bg2)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {stage === 'void' ? 'This endorsement was voided (never sent).' : 'This job is cancelled.'}{lastCancelNote(job.notes) && <span style={{ color: 'var(--text2)' }}>{lastCancelNote(job.notes)}</span>}
          {stage !== 'void' && <button className="sm" onClick={reopenJob}>Reopen</button>}
        </div>)}

      {/* What's next — one sentence */}
      {!cancelled && <div style={{ background: 'var(--bg2)', borderLeft: '4px solid var(--brand)', padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 14 }}>
        <strong>What’s next:</strong> {oteNextStep(job, { deadline })}
      </div>}

      {/* ── Workflow tabs (as Internal Audit) ── */}
      <div className="page-tabs">
        {[['plan', 'Plan & Prepare', !job.audit_plan_sent_at && !cancelled],
          ['report', 'Job & Endorsement', ['awaiting_payment', 'ready_to_send', 'not_endorsed'].includes(stage) || deadlineDue]].map(([v, l, dot]) => (
          <button key={v} onClick={() => setJtab(v)} className={tab === v ? 'page-tab active' : 'page-tab'}>
            {l}{dot && <span title="Action pending" className="page-tab-dot" />}
          </button>))}
      </div>

      <fieldset className={mode === 'view' ? 'view-mode' : undefined} style={{ border: 0, margin: 0, padding: 0, minInlineSize: 0 }}>
      {tab === 'plan' && <div className="plan-cards">
        {/* ── 1 · Client & quotation: read-only reference ── */}
        <div className="card">
          <h2 className="card-title">Client &amp; quotation</h2>
          <div className="info-rows">
            <div className="info-row"><div className="info-label">Contact (OTE)</div><div>{pic.name || '—'}{pic.phone ? ` · ${pic.phone}` : ''} · {pic.email || <span style={{ color: 'var(--danger)' }}>No email</span>}</div></div>
            <div className="info-row"><div className="info-label">UEN · HQ address</div><div>{client.uen || '—'} · {oneLine(client.address || '') || '—'}</div></div>
            <div className="info-row"><div className="info-label">Fee</div><div>{job.quote_amount ? `S$${Number(job.quote_amount).toLocaleString('en-SG', { minimumFractionDigits: 2 })} w/o GST` : '—'}{job.signed_date ? ` · signed ${fmtDay(job.signed_date)}` : ''}</div></div>
            <div className="info-row"><div className="info-label">Work activities from the client</div>
              <div>{!liveRows.length && job.form_data?.activities_later && <div style={{ color: '#854F0B' }}>The client will send the list later — forward it to the auditor; he keys it in on the day.</div>}
                <details><summary style={{ cursor: 'pointer' }}>{liveRows.length} activit{liveRows.length === 1 ? 'y' : 'ies'} — the auditor checks these with the client on site</summary>
                {liveRows.map((r, i) => (
                  <div key={r.id} style={{ fontSize: 14, padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                    <div style={{ color: 'var(--text2)' }}>{i + 1}. {[r.occupation, r.department, r.no_affected ? `${r.no_affected} affected` : ''].filter(Boolean).join(' · ') || '—'}</div>
                    <div style={{ whiteSpace: 'pre-line' }}>{r.work_activities}</div>
                  </div>))}
              </details></div></div>
          </div>
        </div>

        {/* ── 2 · Job details: everything the office fills in ── */}
        <div className="card plan-form">
          <h2 className="card-title">Job details</h2>
          <div className="grid-2">
            <div className="field"><label>Job date *</label>
              <input type="date" value={f.audit_date} disabled={!planEditable} onChange={e => setF(x => ({ ...x, audit_date: e.target.value }))} /></div>
            <div className="field"><label>Start time *</label>
              <input type="time" value={f.time} disabled={!planEditable} onChange={e => setF(x => ({ ...x, time: e.target.value }))} /></div>
          </div>
          {/* Site: NEVER filled in for the office (as bizSAFE / Internal Audit) — typed, or copied from the HQ address. */}
          <div className="field">
            <label>Site address *
              <span className="edit-only" style={{ color: 'var(--brand)', cursor: 'pointer', fontWeight: 400, marginLeft: 8 }}
                onClick={() => client.address ? setF(x => ({ ...x, site: oneLine(client.address) }))
                  : toast('No HQ address for this client', 'warning', { detail: 'Add it on the Clients page, or type the site here' })}>
                Copy HQ address{client.address && f.site === oneLine(client.address) ? ' ✓' : ''}</span></label>
            <input value={f.site} disabled={!planEditable} onChange={e => setF(x => ({ ...x, site: oneLine(e.target.value) }))} />
          </div>
          <div className="field"><label>WSH auditor *</label>
            <select value={f.auditor_id} disabled={!auditorEditable} onChange={e => setF(x => ({ ...x, auditor_id: e.target.value }))}>
              <option value="">— choose —</option>
              {eligible.map(a => <option key={a.id} value={a.id}>{a.name}{sigs.has(a.id) ? '' : ' (no signature yet)'}</option>)}
            </select>
            {f.auditor_id && !sigs.has(f.auditor_id) && <div style={{ fontSize: 14, color: '#854F0B', marginTop: 4 }}>⚠ No signature on file for this auditor — upload it in Personnel → Evidence before the job.</div>}
            <div className="edit-only" style={{ fontSize: 13, color: 'var(--text3)', marginTop: 4 }}>Only approved auditors whose WSH registration is valid on the job date are listed. The auditor is emailed when you save.</div>
          </div>
        </div>

        {/* ── 3 · Send & replies (as Internal Audit) ── */}
        {!cancelled && (stage === 'scheduled' || stage === 'confirmed') && (
          <div className="card">
            <h2 className="card-title">Send &amp; replies</h2>
            {mode === 'edit' && missingList().length > 0 && <div className="plan-still">Still needed: {missingList().join(', ')}</div>}
            {mode === 'edit' && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={saveDraft} disabled={busy === 'save'} style={{ background: dirty ? 'var(--warn)' : undefined, color: dirty ? '#fff' : undefined, borderColor: dirty ? 'var(--warn)' : undefined }}>
                {busy === 'save' ? 'Saving…' : dirty ? '● Save draft' : 'Save draft'}</button>
              <button className="primary" disabled={busy === 'plan' || dirty} onClick={sendConfirmation}
                style={dirty ? { background: 'var(--bg2)', color: 'var(--text3)', borderColor: 'var(--border)', cursor: 'not-allowed' } : undefined}>
                {busy === 'plan' ? 'Sending…' : job.audit_plan_sent_at ? '📤 Send updated confirmation' : '📤 Send confirmation'}</button>
              {dirty && <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--warn)' }}>● Unsaved changes</span>}
            </div>}
            {mode === 'edit' && <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 8 }}>Send copies the client email — paste it into your email to {pic.email || 'the client'} in Outlook. AuditPM then reminds the client 7 days before the job.</div>}
            {mode === 'view' && !job.audit_plan_sent_at && <div style={{ fontSize: 14, color: 'var(--text2)' }}>Not sent yet — click <strong>Edit</strong>, fill in the job details, then <strong>📤 Send confirmation</strong>.</div>}
            {!dirty && job.audit_plan_sent_at && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: mode === 'edit' ? 12 : 0 }}>
                <span style={{ fontSize: 14, color: 'var(--text2)' }}>Last sent {fmtDT(job.audit_plan_sent_at)}</span>
                <button className="sm" onClick={copyAgain}>Copy client email again</button>
              </div>)}
          </div>
        )}
      </div>}

      {tab === 'report' && <>
        {/* ── Payment (as Internal Audit: record it any time; the clearance check when sending) ── */}
        {!cancelled && (
          <div className="card action-live" style={{ marginBottom: 14 }}>
            <h2 className="card-title">Payment</h2>
            {paid
              ? <div style={{ fontSize: 14 }}><span style={{ color: 'var(--brand)' }}>✓</span> Payment received{job.invoice_clearance_checked_by_name ? ` — recorded by ${job.invoice_clearance_checked_by_name}` : ''}{job.invoice_clearance_checked_at ? ` on ${fmtDT(job.invoice_clearance_checked_at)}` : ''}</div>
              : job.invoice_clearance_checked_at
              ? <div style={{ fontSize: 14, color: '#854F0B' }}>⚠ Released before payment{job.invoice_clearance_checked_by_name ? ` — by ${job.invoice_clearance_checked_by_name}` : ''} on {fmtDT(job.invoice_clearance_checked_at)}
                  {job.invoice_clearance_reason ? <div style={{ marginTop: 4, color: 'var(--text2)' }}>Reason: {job.invoice_clearance_reason}</div> : null}
                  {stage !== 'awaiting_payment' && <div style={{ marginTop: 8 }}><button className="sm" disabled={!!busy} onClick={() => { if (window.confirm('Record that payment has now been received?')) run('paid', { action: 'record-payment', cleared: true }, 'Payment recorded') }}>Payment received</button></div>}</div>
              : stage !== 'awaiting_payment' && <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 14, color: 'var(--text2)' }}>Payment not recorded yet</span>
                  <button className="sm" disabled={!!busy} onClick={() => { if (window.confirm('Record that payment has been received?')) run('paid', { action: 'record-payment', cleared: true }, 'Payment recorded') }}>Payment received</button>
                </div>}
            {/* While the endorsement (or the not-endorsed notice) is waiting to go out — ALWAYS the Send button. */}
            {['awaiting_payment', 'ready_to_send', 'not_endorsed'].includes(stage) && (
              <div style={{ marginTop: paid || job.invoice_clearance_checked_at ? 10 : 0 }}>
                <div className="field" style={{ maxWidth: 420 }}><label>{stage === 'not_endorsed' ? 'Send the not-endorsed notice to' : 'Send the endorsement to'}</label><input value={sendTo} onChange={e => setSendTo(e.target.value)} /></div>
                <button className="primary" disabled={!!busy} onClick={() => {
                  if (!sendTo.trim()) return toast("Enter the client's email", 'warning')
                  const kind = stage === 'not_endorsed' ? 'notice' : 'endorsement'
                  if (job.invoice_clearance_checked_at) { if (window.confirm(`Send the ${kind === 'notice' ? 'not-endorsed notice' : 'endorsement'} to ${sendTo.trim()}? (query@ in CC)`)) confirmSend(kind); return }
                  setPayCleared(true); setPayReason(''); setPayModal(kind)
                }}>{busy === 'send' || busy === 'pay' ? 'Sending…' : stage === 'not_endorsed' ? 'Send not-endorsed notice' : 'Send endorsement'}</button>
              </div>)}
          </div>
        )}

        {/* ── Waiting for the client (items still needed) ── */}
        {(issues.length > 0 || stage === 'awaiting_closeout') && (
          <div className="card action-live" style={{ marginBottom: 14 }}>
            <h2 className="card-title">Items still needed from the client</h2>
            {issues.map(i => (
              <div key={i.id} style={{ fontSize: 14, padding: '5px 0', borderTop: '1px solid var(--border)' }}>
                <span className={`badge ${i.status === 'open' ? 'badge-warn' : 'badge-ok'}`} style={{ marginRight: 6 }}>{i.status === 'open' ? 'Open' : 'Closed'}</span>
                {i.client_action || i.finding}
                {i.status === 'closed' && <span style={{ color: 'var(--text2)' }}> · closed by {i.closed_by} on {fmtDay(i.closed_at)}</span>}
              </div>))}
            {openIssues.length > 0 && (
              <div style={{ fontSize: 14, color: 'var(--text2)', marginTop: 8 }}>
                The client emails the documents to query@ — forward them to <strong>{job.auditor_name || 'the auditor'}</strong>. The auditor uploads them and closes each item.
                {stage === 'awaiting_closeout' && deadline && <> Deadline: <strong>{fmtDay(sgDayOf(deadline))}</strong> (AuditPM reminds the client on day 7, 14 and 21).</>}
              </div>)}
            {deadlineDue && (
              <div style={{ background: '#fff4e0', padding: '10px 12px', borderRadius: 8, fontSize: 14, marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                The client has not sent everything by the deadline.
                <button className="sm" disabled={!!busy} onClick={() => run('ext', { action: 'extend', days: 7 }, 'Client given 7 more days')}>Give 7 more days</button>
                <button className="sm" disabled={!!busy} onClick={() => run('ext', { action: 'extend', days: 14 }, 'Client given 14 more days')}>Give 14 more days</button>
                <button className="sm danger" disabled={!!busy} onClick={() => {
                  if (window.confirm('Close this job as NOT endorsed? A not-endorsed report is kept, and you then send the client the not-endorsed notice.'))
                    run('close', { action: 'close-not-endorsed', confirm: 'CLOSE' }, 'Closed as not endorsed')
                }}>Close as not endorsed</button>
              </div>)}
            {stage === 'awaiting_closeout' && !deadlineDue && isAdmin && (
              <div style={{ marginTop: 10 }}>
                <button className="sm" disabled={!!busy} onClick={() => { const r = askReason('Close this job as NOT endorsed before the deadline?'); if (r) run('close', { action: 'close-not-endorsed', confirm: 'CLOSE', reason: r }, 'Closed as not endorsed') }}>Close now as not endorsed (Admin)</button>
              </div>)}
          </div>
        )}

        {/* ── Reports ── */}
        <div className="card action-live" style={{ marginBottom: 14 }}>
          <h2 className="card-title">Reports</h2>
          {!reports.length && OPEN_STAGES.includes(stage) && <div style={{ fontSize: 14, color: 'var(--text3)' }}>Waiting for the auditor’s endorsement and report</div>}
          {['report', 'endorsement'].some(k => latest(k)) && (
            <table style={{ fontSize: 14, marginBottom: 10 }}><tbody>
              {['endorsement', 'report'].map(k => { const r = latest(k); if (!r) return null; return (
                <tr key={k}>
                  <td>{KIND[k]} <span style={{ color: 'var(--text2)' }}>v{r.version}</span></td>
                  <td style={{ color: 'var(--text2)' }}>{fmtDT(r.created_at)}</td>
                  <td style={{ color: r.status === 'error' ? 'var(--danger)' : 'var(--text2)' }} title={r.error || ''}>{STATUS[r.status] || r.status}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.status === 'issued' && <button className="sm" onClick={() => openReport(r)}>Open</button>}
                    {(r.status === 'error' || (r.status === 'preparing' && Date.now() - new Date(r.created_at).getTime() > 10 * 60 * 1000)) && !cancelled && <button className="sm" disabled={!!busy} onClick={() => run('rr', { action: 'rerender', reportIds: [r.id] }, 'Preparing again')}>Prepare again</button>}
                  </td>
                </tr>) })}
            </tbody></table>)}
          {stage === 'completed' && <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 10 }}>Endorsement sent to the client.</div>}
          {stage === 'signing' && <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 10 }}>
            {signingStuck ? 'Signing seems to have stopped.' : 'The auditor is signing — please reload in a few minutes.'}
            {signingStuck && isAdmin && <button className="sm" style={{ marginLeft: 8 }} disabled={!!busy} onClick={() => { const r = askReason('Free the stuck signing? The job goes back to the auditor to sign again.'); if (r) run('fixsign', { action: 'reopen', reason: r }, 'Back with the auditor') }}>Fix stuck signing (Admin)</button>}
          </div>}
          {stage === 'sending' && <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 10 }}>
            {sendingStuck ? 'Sending seems to have stopped.' : 'Sending to the client — please reload in a few minutes.'}
            {sendingStuck && isAdmin && <button className="sm" style={{ marginLeft: 8 }} disabled={!!busy} onClick={() => { const r = askReason('Fix the stuck send? AuditPM checks the email log: if the email went, the job is finished; if not, you can send again.'); if (r) run('fix', { action: 'reopen', reason: r }, 'Fixed') }}>Fix stuck send (Admin)</button>}
          </div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {stage === 'completed' && <button className="sm" disabled={!!busy} onClick={() => {
              const to = window.prompt('Send a fresh 12-month download link to:', sendTo || '')
              if (to && to.trim()) run('links', { action: 'resend-link', to: to.trim() }, 'New link sent')
            }}>Resend Link to Client</button>}
            {isAdmin && ['awaiting_payment', 'ready_to_send', 'not_endorsed', 'completed'].includes(stage) && (
              <button className="sm" disabled={!!busy} onClick={() => { const r = askReason('Reopen for the auditor? The auditor signs again; earlier versions are kept.'); if (r) run('reopen', { action: 'reopen', reason: r }, 'Reopened for the auditor') }}>Reopen for the auditor (Admin)</button>)}
            {isAdmin && ['awaiting_payment', 'ready_to_send'].includes(stage) && (
              <button className="sm danger" disabled={!!busy} onClick={() => { const r = askReason('Void this endorsement? It will never be sent, and the signed quotation is cancelled.'); if (r) run('void', { action: 'void', reason: r }, 'Voided') }}>Void (Admin)</button>)}
            {isAdmin && stage === 'completed' && (
              <button className="sm" disabled={!!busy} onClick={() => {
                const r = askReason('Re-issue the endorsement for a clerical fix (company name, UEN or address)? Same signer, date and result.'); if (!r) return
                const name = window.prompt('Company name on the endorsement:', client.name || ''); if (name === null) return
                const uen = window.prompt('UEN:', client.uen || ''); if (uen === null) return
                const address = window.prompt('Address on the endorsement:', job.audit_site_address || oneLine(client.address || '')); if (address === null) return
                run('reissue', { action: 'reissue', reason: r, companyName: name, uen, address }, 'Re-issue started')
              }}>Re-issue endorsement (Admin)</button>)}
            {isAdmin && (stage === 'in_progress' || stage === 'awaiting_closeout') && (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <select value={newAuditor} onChange={e => setNewAuditor(e.target.value)} style={{ width: 'auto' }}>
                  <option value="">Change auditor (Admin)…</option>
                  {eligible.filter(a => a.id !== job.auditor_id).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {newAuditor && <button className="sm" disabled={!!busy} onClick={async () => {
                  const r = askReason('Change the auditor after the job has started?'); if (!r) return
                  if (await run('assign', { action: 'assign', auditorId: newAuditor, reason: r }, 'Auditor changed')) setNewAuditor('')
                }}>Change</button>}
              </span>)}
          </div>
        </div>

        {/* ── Records & history (as Internal Audit) ── */}
        {historyRows.length > 0 && (
          <div className="card" style={{ marginTop: 24 }}>
            <h2 className="card-title">Records &amp; history</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {historyRows.map((r, i) => <div key={i} style={{ fontSize: 14 }}><span style={{ color: 'var(--brand)' }}>✓</span> {r.text} · {fmtDT(r.at)}</div>)}
            </div>
          </div>)}

        {/* ── Session evidence (INTERNAL ONLY, as Internal Audit) ── */}
        {(photo || noPhotoReason || signedStages.includes(stage)) && (
          <div className="card" style={{ marginTop: 16 }}>
            <h2 className="card-title">Session evidence <span className="card-title-note">(internal only)</span></h2>
            {photo ? (<>
              <div style={{ fontSize: 14, marginBottom: 12 }}>Photo consent: <strong>{consent === 'No' ? 'No' : consent ? 'Yes' : 'not recorded'}</strong><span style={{ color: 'var(--text2)' }}> (uploaded {fmtDT(photo.created_at)})</span></div>
              <button className="sm" onClick={async () => { const { data } = await supabase.storage.from('audit-evidence').createSignedUrl(photo.storage_path, 120); if (data?.signedUrl) window.open(data.signedUrl, '_blank') }}>View</button>
            </>) : noPhotoReason ? (
              <div style={{ padding: '10px 14px', background: '#FFF3E0', border: '1px solid #E6A817', borderRadius: 8, fontSize: 14, color: '#6b4e00' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Session photo not uploaded</div><div style={{ whiteSpace: 'pre-wrap' }}>{noPhotoReason}</div>
              </div>
            ) : <div style={{ padding: '10px 14px', background: '#FDECEA', border: '1px solid #E24B4A', borderRadius: 8, fontSize: 14, color: '#791F1F' }}>⚠ No session photo or reason recorded.</div>}
          </div>)}
      </>}
      </fieldset>

      {/* Payment Clearance Check — as Internal Audit */}
      {payModal && (
        <div className="modal-overlay" onClick={() => !busy && setPayModal(null)}>
          <div className="modal action-live" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: 14 }}>Payment Clearance Check</h2></div>
            <p style={{ fontSize: 14, color: 'var(--text2)', margin: '4px 0 12px' }}>Before sending the {payModal === 'notice' ? 'not-endorsed notice' : 'endorsement'} to {sendTo.trim() || 'the client'}, please confirm payment status.</p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', marginBottom: 10 }}>
              <input type="checkbox" checked={payCleared} onChange={e => setPayCleared(e.target.checked)} />
              Payment has been received.
            </label>
            {!payCleared && (
              <div className="field" style={{ margin: 0 }}>
                <label>Reason for releasing before payment *</label>
                <textarea rows={3} value={payReason} onChange={e => setPayReason(e.target.value)} placeholder="Example: Client urgent submission deadline, management approval obtained, etc." />
                {!payReason.trim() && <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>A reason is required when payment has not been received.</div>}
              </div>)}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="sm" disabled={!!busy} onClick={() => setPayModal(null)}>Cancel</button>
              <button className="primary sm" disabled={!!busy || (!payCleared && !payReason.trim())} onClick={() => confirmSend(payModal)}>
                {payModal === 'notice' ? 'Confirm & send notice' : 'Confirm & send endorsement'}</button>
            </div>
          </div>
        </div>)}
    </div>
  )
}
